import React, { useEffect } from "react";
import { useState } from "react";
import {
  EmailAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  linkWithPopup,
  GoogleAuthProvider,
  signInWithCredential,
  signInWithEmailLink,
  signInWithPopup,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, Providers } from "../config/firebase";
import { FiMail } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { UserApi } from "../api/UserApi";
import toast from "react-hot-toast";

interface Props { }

const LoginPage = ({ }: Props) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [email, setEmail] = useState("");

  const user = auth.currentUser;
  const isDurableUser = !!user && !user.isAnonymous;

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      setInfoMessage("Verifying sign-in code. Please wait...");

      const params = new URLSearchParams(window.location.search);
      const email = params.get("email");
      if (!email) {
        toast.error("Missing email in the sign-in link.");
        return
      }
      const credential = EmailAuthProvider.credentialWithLink(email, window.location.href);
      const signIn = auth.currentUser?.isAnonymous ?
        linkWithCredential(auth.currentUser, credential) :
        signInWithEmailLink(auth, email, window.location.href);
      signIn
        .then((result) => {
          navigate("/banana-stand");
        })
        .catch(async (error) => {
          if (error.code === "auth/credential-already-in-use") {
            await signInWithEmailLink(auth, email, window.location.href);
            navigate("/banana-stand");
            return;
          }
          setErrorMessage(error.code + ": " + error.message);
          setInfoMessage("")
          setDisabled(false);
        });
    }
  }, [])

  const handleEmailLogin = (event: React.FormEvent) => {
    event.preventDefault();
    setDisabled(true);

    UserApi.sendSignInLinkToEmail(email)
      .then(() => {
        setDisabled(false);
        setSuccessMessage("Check your email for the sign-in link!");
      })
      .catch((error) => {
        setErrorMessage(error.message);
        setDisabled(false);
      });
  }

  const signInWithGoogle = () => {
    setDisabled(true);
    const signIn = auth.currentUser?.isAnonymous ?
      linkWithPopup(auth.currentUser, Providers.google) :
      signInWithPopup(auth, Providers.google);
    signIn
      .then(() => {
        setDisabled(false);
        navigate("/banana-stand");
      })
      .catch(async (error) => {
        if (error.code === "auth/credential-already-in-use") {
          const credential = GoogleAuthProvider.credentialFromError(error);
          if (credential) {
            await signInWithCredential(auth, credential);
            setDisabled(false);
            navigate("/banana-stand");
            return;
          }
        }
        const ignoreErrors = ["auth/popup-closed-by-user", "auth/cancelled-popup-request"];
        if (!ignoreErrors.includes(error.code)) {
          setErrorMessage(error.code + ": " + error.message);
        }
        setDisabled(false);
      });
  };

  useEffect(() => {
    if (isDurableUser) {
      navigate("/banana-stand");
    }
  }, [isDurableUser, navigate]);
  
  return (
    <>
      <title>Enter Bananaverse | Tuttobanana</title>
      <meta name="description" content="Enter the Bananaverse. Log in or create your account instantly with email or Google. No passwords, just smiles." />

      <div className="min-h-screen flex items-start justify-center bg-gray-100 px-6 pt-16">
        <div className="w-full max-w-md bg-white shadow-md rounded-lg p-8">
          {!successMessage && !infoMessage && <>
            <h1 className="text-2xl font-bold mb-6 text-center text-gray-900">Enter the Bananaverse 🍌</h1>

            {errorMessage && (
              <div className="mb-4 text-sm text-red-600 bg-red-100 border border-red-300 px-4 py-2 rounded-md">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-yellow-400 focus:border-yellow-400"
                />
              </div>
              <button
                disabled={disabled}
                type="submit"
                className={`w-full flex items-center justify-center gap-2 bg-yellow-400 text-gray-900 font-semibold py-2 rounded-md hover:bg-yellow-300 transition ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <FiMail size={18} />
                Continue with Email
              </button>
              <p className="text-sm text-gray-500 text-center">
                {user?.isAnonymous ? "Secure this browser identity so it works on your other devices." : "If you don't have an account, we'll create it for you."}
              </p>
            </form>

            <div className="my-6 flex items-center">
              <div className="flex-grow h-px bg-gray-200" />
              <span className="px-4 text-gray-400 text-sm">or</span>
              <div className="flex-grow h-px bg-gray-200" />
            </div>

            <button
              disabled={disabled}
              onClick={signInWithGoogle}
              className={`w-full flex items-center justify-center gap-2 bg-white border border-gray-300 py-2 rounded-md hover:bg-gray-50 transition ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <FcGoogle size={20} />
              Continue with Google
            </button>
          </>}
          {successMessage && <>
            <div className="text-sm text-green-700 bg-green-100 border border-green-300 px-4 py-2 rounded-md">
              {successMessage}
            </div>
          </>}

          {infoMessage && <>
            <div className="text-sm px-4 py-2 rounded-md">
              {infoMessage}
            </div>
          </>}
        </div>
      </div>
    </>
  );
};

export default LoginPage;
